import { useCallback, useEffect, useMemo, useState } from 'react';
import { withWindow } from '@shared/utils/withWindow';
import type { CategoryFeaturesSectionProps } from './CategoryFeaturesSection';
import type { Category } from '../CategoryShowcase';
import type { CatalogDepartmentEntry } from '../../state/Catalog';
import { useCatalogContext } from '../../state/Catalog';
import { useCatalogRouting } from '../../useCatalogRouting';
import type { ExplodedCategoryProps } from '../../routes/CatalogProductsIndex/components/ExplodedCategory/ExplodedCategory';
import { useCatalogLoggingContext } from '../../Catalog.logging';

export const useCategoryFeaturesSection = ({ isMobile, showcaseExploreAllRef, placement }: CategoryFeaturesSectionProps) => {
  const { selectedCategory } = useCatalogContext();
  const selectedCategoryChildren = (selectedCategory as CatalogDepartmentEntry)?.children;

  const categoryShowcaseList = useMemo<Category[]>(
    () =>
      selectedCategoryChildren
        ? selectedCategoryChildren.map(({ id, publicUrlPath, showcase, isFeatured }) => ({
            id,
            title: showcase?.title ?? '',
            publicUrlPath: publicUrlPath,
            image: showcase?.image?.url ?? '',
            isFeatured: !!isFeatured,
          }))
        : ([] as Category[]),
    [selectedCategoryChildren],
  );
  const featuredCategoriesList = useMemo<ExplodedCategoryProps['subcategories']>(
    () =>
      categoryShowcaseList
        ?.filter(({ isFeatured }) => isFeatured)
        .map(({ id, title, publicUrlPath }) => ({
          id,
          title,
          publicUrlPath,
        })),
    [categoryShowcaseList],
  );

  const { goToCategory } = useCatalogRouting();
  const { trackCategoryNavigateAction } = useCatalogLoggingContext();
  const handleBrowseCategoryClick = useCallback<ExplodedCategoryProps['onBrowseCategoryClick']>(
    (category, navigationSource, skipNavigation) => {
      if (!skipNavigation) goToCategory(category.publicUrlPath);
      trackCategoryNavigateAction({ category, navigationSource, overrides: { placement } });
    },
    [goToCategory, trackCategoryNavigateAction, placement],
  );

  // // If endElementRef is not provided, we'll always show the endElement at the top bar
  const [showTopBarEndElement, setShowTopBarEndElement] = useState(!showcaseExploreAllRef?.current);
  const handleOnScroll = useCallback(() => {
    if (!isMobile) {
      // If endElementRef is provided, we'll just show the endElement when the endElementRef is not visible
      if (showcaseExploreAllRef?.current) {
        const { top: endElementRefTop } = showcaseExploreAllRef.current.getBoundingClientRect();
        setShowTopBarEndElement(endElementRefTop <= 0);
      }
    }
  }, [isMobile, showcaseExploreAllRef]);

  useEffect(() => {
    withWindow(window => {
      window.addEventListener('scroll', handleOnScroll);
    });
    return () => {
      withWindow(window => {
        window.removeEventListener('scroll', handleOnScroll);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { categoryShowcaseList, featuredCategoriesList, selectedCategory, showTopBarEndElement, handleBrowseCategoryClick };
};
